// src/modulos/Parametros/PackagingPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const PACKAGING_COLLECTION = 'parameters/data/embalagem';
const ITEMS_PER_PAGE = 10;

const PackagingPage = () => {
    const can = usePagePermissions('embalagem');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: packagings,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        checkIfInUse,
        refreshData
    } = useFirestore(PACKAGING_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredItems ANTES dos callbacks que o usam
    // ============================================================
    const filteredItems = useMemo(() => {
        if (!packagings) return [];
        if (!searchTerm) return packagings;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return packagings.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [packagings, searchTerm]);

    const totalFilteredItems = filteredItems.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentItems = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredItems, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
        setFieldError('');
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name || '');
        setFormError('');
        setFormSuccess('');
        setFieldError('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete (AGORA filteredItems já está definido)
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            // Verificar se está em uso
            const isInUse = await checkIfInUse(id, 'products', 'packagingId');

            if (isInUse) {
                setFormError(`Não é possível deletar a embalagem "${name}" pois ela está associada a um ou mais produtos.`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setTimeout(() => setFormError(''), 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Embalagem "${name}" deletada com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Usar filteredItems (já definido acima)
                const remainingItems = filteredItems.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Embalagem: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar embalagem. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkIfInUse, deleteDocument, filteredItems, currentPage]);

    // Submissão do Formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');
        setFieldError('');

        if (!nameInput.trim()) {
            setFieldError("O nome da embalagem é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Embalagem editada com sucesso!' : 'Embalagem adicionada com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, addDocument, updateDocument, resetForm]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Validação em tempo real
    const handleNameChange = useCallback((e) => {
        const value = e.target.value;
        setNameInput(value);

        if (value.trim()) {
            setFieldError('');
        } else {
            setFieldError('O nome da embalagem é obrigatório');
        }
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Embalagem'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome da Embalagem <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={handleNameChange}
                        placeholder="Ex: Pote, Lata, Sachê, Caixa"
                        required
                        className={`${styles.input} ${fieldError ? styles.inputError : ''}`}
                        disabled={formLoading}
                        autoFocus
                    />
                    {fieldError && <span className={styles.errorHint}>{fieldError}</span>}
                    <small className={styles.fieldHint}>
                        Nome que será exibido nos produtos e relatórios
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a embalagem "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Embalagens"
                actionText="Nova Embalagem"
                onAction={handleNewClick}
                disabled={formLoading || deleteLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome da embalagem..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading || deleteLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentItems.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📦'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma embalagem cadastrada'
                            }
                            message={
                                searchTerm
                                    ? 'Tente usar outro termo de busca'
                                    : "Clique em 'Nova Embalagem' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome da Embalagem</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading || deleteLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading || deleteLoading}
                                                        >
                                                            {deleteLoading ? '...' : 'Deletar'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading || deleteLoading}
                                itemsLabel="embalagens"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PackagingPage;